import React, { useEffect, useState } from 'react';
import { Modal, message } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import EasyInput from '../EasyInput/EasyInput';
import EasyButton from '../EasyButton/EasyButton';
import ShowHint from '../ShowHint/ShowHint';
import { MixApi } from '../../apis/MixApi';
import { globalStore } from '../../GlobalStore';
import TianaiCaptchaModal from '../TianaiCaptchaModal/TianaiCaptchaModal';
import './LoginModal.css';
import { LoginModalConfig } from './LoginModalConfig';

interface LoginModalProps {
  visible: boolean;
  onClose: () => void;
}

const {
  inputHeightPx,
  primaryColor,
  primaryColorLight,
  logoRowPaddingTopPx,
  logoRowPaddingBottomPx,
  borderRadiusPx,
} = LoginModalConfig;
const inputHeight = `${inputHeightPx}px`;

const LoginModal: React.FC<LoginModalProps> = ({ visible, onClose }) => {
  const [phoneNo, setPhoneNo] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [areaCode, setAreaCode] = useState('+86');
  const [areaCodeOptions, setAreaCodeOptions] = useState<Array<{ code: string; dialCode: string; name: string; }>>([]);
  const [countDown, setCountDown] = useState(0);
  const [isNewUser, setIsNewUser] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [hintMessage, setHintMessage] = useState('');
  const [hintType, setHintType] = useState<'success' | 'error' | 'warning' | 'info'>('info');
  const [captchaVisible, setCaptchaVisible] = useState(false);

  useEffect(() => {
    loadAreaCodes();
  }, []);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countDown > 0) {
      timer = setInterval(() => {
        setCountDown(prev => prev - 1);
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [countDown]);

  const loadAreaCodes = async () => {
    const codes = await MixApi.getPhoneAreaCode();
    setAreaCodeOptions(codes);
  };

  const showHintMessage = (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setHintMessage(message);
    setHintType(type);
    setShowHint(true);
  };

  // 清空所有输入框
  const clearForm = () => {
    setPhoneNo('');
    setPassword('');
    setConfirmPassword('');
    setVerifyCode('');
    setIsNewUser(false);
    setCountDown(0);
    setShowHint(false);
  };

  // 组合区号和电话号码
  const getFullPhoneNumber = (phone: string, area: string): string => {
    if (!phone) return '';
    // 如果电话号码已经包含区号，直接返回
    if (phone.startsWith('+')) {
      return phone;
    }
    // 组合区号和电话号码
    return area + phone;
  };

  const handleSendCode = () => {
    if (!phoneNo) {
      message.error('请输入手机号');
      return;
    }
    setCaptchaVisible(true);
  };

  const handleCaptchaSuccess = async (captchaToken: string) => {
    try {
      const fullPhoneNo = getFullPhoneNumber(phoneNo, areaCode);
      const sent = await MixApi.sendPhoneCode(fullPhoneNo, captchaToken);
      if (!sent) {
        message.error('发送验证码失败');
        return;
      }
      message.success('验证码已发送');
      setCountDown(60);
    } catch (error) {
      message.error('发送验证码失败');
    }
  };

  const handleLogin = async () => {
    if (!phoneNo) {
      message.error('请输入手机号');
      return;
    }

    // 组合完整的电话号码（包含区号）
    const fullPhoneNo = getFullPhoneNumber(phoneNo, areaCode);

    if (areaCode === '+86') {
      if (!verifyCode) {
        message.error('请输入验证码');
        return;
      }
      try {
        await globalStore.loginWithCode(fullPhoneNo, verifyCode);
        message.success('登录成功');
        clearForm();
        onClose();
      } catch (error: any) {
        const errorMsg = error?.message || '登录失败';
        console.log("登录失败，错误信息：", errorMsg);
        message.error("登录失败，验证码或者账号密码错误！");
      }
    } else {
      if (!password) {
        message.error('请输入密码');
        return;
      }

      // 如果是新用户，需要确认密码
      if (isNewUser) {
        if (!confirmPassword) {
          message.error('请再次输入密码');
          return;
        }
        if (password !== confirmPassword) {
          message.error('两次输入的密码不一致');
          return;
        }
        // 密码一致，调用注册接口
        try {
          // 这里需要调用注册接口，暂时用登录接口代替
          await globalStore.login(fullPhoneNo, password);
          message.success('注册成功');
          clearForm();
          onClose();
        } catch (error: any) {
          const errorMsg = error?.message || '注册失败';
          message.error(errorMsg);
        }
      } else {
        try {
          const result = await globalStore.login(fullPhoneNo, password);
          if (result) {
            message.success('登录成功');
            clearForm();
            onClose();
          }
        } catch (error: any) {
          if (error.message === 'NOUSER') {
            // 新用户，显示确认密码输入框
            setIsNewUser(true);
            showHintMessage('检测到新用户，请再次输入密码确认', 'info');
          } else {
            const errorMsg = error?.message || '登录失败';
            message.error(errorMsg);
          }
        }
      }
    }
  };

  const handleSellerClick = () => {
    window.open('https://seller.yuanling.com', '_blank');
  };

  return (
    <>
      <Modal 
        open={visible}
        onCancel={()=>{
          clearForm();
          onClose();
        }} 
        footer={null}
        width={520}
        centered
        className="login-modal"
        styles={{
          content: {
            borderRadius: borderRadiusPx,
            overflow: 'hidden',
            ['--login-border-radius' as string]: `${borderRadiusPx}px`,
          },
        }}
      >
        <div
          className="login-form"
          style={{
            ['--login-primary' as string]: primaryColor,
            ['--login-primary-light' as string]: primaryColorLight,
            ['--login-input-height' as string]: inputHeight,
            ['--login-logo-padding-top' as string]: `${logoRowPaddingTopPx}px`,
            ['--login-logo-padding-bottom' as string]: `${logoRowPaddingBottomPx}px`,
            ['--login-border-radius' as string]: `${borderRadiusPx}px`,
          }}
        >
          {/* Logo */}
          <div className="login-logo">
            <img src="/logo.svg" style={{width: 40, height: 40}} alt="网站Logo" />
            <span style={{fontSize: 20, marginLeft: 10, fontWeight: 300, color: '#000'}}>登录/注册圆领</span>
          </div>

          {/* 提示信息和卖家链接 */}
          <div className="login-tip-row">
            <span className="login-tip">未注册的手机号验证后将自动创建账号</span>
            <span className="seller-link" onClick={handleSellerClick}>
              我是卖家 <RightOutlined />
            </span>
          </div>
          
          <div style={{ height: '16px' }}></div>
          
          <EasyInput
            label="手机号"
            type={5}
            value={phoneNo}
            onChange={setPhoneNo}
            areaCode={areaCode}
            onAreaCodeChange={setAreaCode}
            areaCodeOptions={areaCodeOptions}
            height={inputHeight}
          />
          <div style={{ display: 'flex', alignItems: 'center', height: '20px' }}></div>
          {areaCode === '+86' ? (
            <div style={{ position: 'relative' }}>
              <EasyInput
                label="验证码"
                type={1}
                value={verifyCode}
                onChange={setVerifyCode}
                height={inputHeight}
              />
              <div 
                className={`verify-code-text ${countDown > 0 ? 'disabled' : ''}`}
                onClick={countDown > 0 ? undefined : handleSendCode}
              >
                {countDown > 0 ? `${countDown}s` : '发送'}
              </div>
            </div>
          ) : (
            <>
              <EasyInput
                label="密码"
                type={4}
                value={password}
                onChange={setPassword}
                height={inputHeight}
              />
              {isNewUser && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', height: '16px' }}></div>
                  <EasyInput
                    label="再次输入密码"
                    type={4}
                    value={confirmPassword}
                    onChange={setConfirmPassword}
                    height={inputHeight}
                  />
                </>
              )}
            </>
          )}

          <EasyButton
            width="100%"
            height={inputHeightPx}
            type="primary"
            backgroundColor={primaryColor}
            onClick={handleLogin}
            marginTop={20}
          >
            {isNewUser ? '注册' : '登录'}
          </EasyButton>
        </div>
      </Modal>
      
      <ShowHint
        visible={showHint}
        message={hintMessage}
        type={hintType}
        duration={3000}
        onClose={() => setShowHint(false)}
      />

      <TianaiCaptchaModal
        visible={captchaVisible}
        onClose={() => setCaptchaVisible(false)}
        onSuccess={handleCaptchaSuccess}
      />
    </>
  );
};

export default LoginModal; 